'use client';

import { forwardRef, useContext, useId } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { ToneIcon } from '../tone-icon';
import { PopoverRootContext } from './context/popover-root.context';
import { popoverHeaderRecipe } from './styles/popover-header.styles';
import type { PopoverHeaderPropsInterface } from './types/popover-header.types';

/**
 * @since 10.0.0
 */
export const PopoverHeader = forwardRef<HTMLDivElement, PopoverHeaderPropsInterface>(
  (props: PopoverHeaderPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { popover, tone } = useContext(PopoverRootContext);
    const id = useId();
    const styles = css(popoverHeaderRecipe.raw({ tone }), cssProp);

    useIsomorphicLayoutEffect(() => {
      popover.setLabelId(id);
      return () => popover.setLabelId(undefined);
    }, [id, popover.setLabelId]);

    return (
      <div
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Popover.Header"
        id={id}
        className={cx('c11n', styles, className)}
        {...rest}
      >
        {tone && tone !== 'neutral' && <ToneIcon tone={tone} />}
        {children}
      </div>
    );
  },
);

PopoverHeader.displayName = 'Popover.Header';
